import { Component, Inject, OnDestroy, Output, TemplateRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FieldType } from '@app/shared/constants';
import { FilterItem, FilterOutput, FiltersOutput, FilterType } from '@app/shared/models/filter.interface';
import { RSQLOperator } from '@app/shared/models/rsql/rsql.operators';
import { EventEmitter } from 'events';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface FilterContent {
  content: any;
}

// FIXME: this component may be obsolete. Check if must be removed.
@Component({
  selector: 'vfs-filter-base',
  templateUrl: './filter-base.component.html',
  styleUrls: ['./filter-base.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FilterBaseComponent implements OnDestroy {
  filterTypes = FilterType;
  formGroup: FormGroup;
  filters: FilterItem[];
  filtersOutput = {} as FiltersOutput;
  observableCount = 0;

  /** Subject used to unsuscribe from the observables */
  destroy$: Subject<boolean> = new Subject<boolean>();

  withTemplate = this.data.content instanceof TemplateRef;
  // With data
  withData = !(this.data.content instanceof TemplateRef);

  @Output() clearOutput = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<FilterBaseComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterContent
  ) {
    this.filtersOutput.filters = [] as FilterOutput[];
    if (this.withData) {
      // Create filter items
      this.filters = this.data.content;
      this.formGroup = this.formBuilder.group({});

      // Loop items just to find the number of observables. The "Accept" button
      // will be active when all the observables are completed...
      this.filters.forEach(item => {
        if (item.observable) {
          this.observableCount++;
        }
      });

      this.filters.forEach(item => {
        this.addFilter(item);
      });
    }
  }

  close() {
    this.dialogRef.close(this.createFiltersOutput(false));
  }

  accept() {
    this.dialogRef.close(this.createFiltersOutput(true));
  }

  clear() {
    if (this.withData) {
      this.formGroup.reset(this.getInitialValues());
    }
  }

  protected getInitialValues() {
    let initialValues = {};

    this.filters
      .filter(filter => filter.defaultValue)
      .forEach(filterInitial => {
        initialValues[filterInitial.field.formControlName] = filterInitial.defaultValue;
      });

    return initialValues;
  }

  // Filter with data
  addFilter(item: FilterItem) {
    this.formGroup.addControl(
      item.field.formControlName,
      new FormControl(item.value ? item.value : this.getDefaultValue(item))
    );
    if (item.observable) {
      // An observable item must wait until the data is available...
      item.observable.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.observableCount--;
        if (this.isTypeSelect(item.type)) {
          item.options = data;
        }
      });
    }
  }

  getDefaultValue(item: FilterItem) {
    switch (item.type) {
      case FilterType.CHECKBOX:
        return false;
      case FilterType.DATE:
        return null;
      default:
        return '';
    }
  }

  isTypeSelect(filterType: FilterType) {
    return (
      filterType === FilterType.SELECT_STANDARD ||
      filterType === FilterType.SELECT_MULTIPLE ||
      filterType === FilterType.SELECT_SEARCHABLE ||
      filterType === FilterType.SELECT_MULTIPLE_SEARCHABLE
    );
  }

  isTypeSelectMultiple(filterType: FilterType) {
    return filterType === FilterType.SELECT_MULTIPLE || filterType === FilterType.SELECT_MULTIPLE_SEARCHABLE;
  }

  isTypeSelectSearchable(filterType: FilterType) {
    return filterType === FilterType.SELECT_SEARCHABLE || filterType === FilterType.SELECT_MULTIPLE_SEARCHABLE;
  }

  createFiltersOutput(accept: boolean): FiltersOutput {
    this.filtersOutput.accept = accept;
    if (this.withData) {
      const controls = Object.entries(this.formGroup.controls);

      controls.forEach(control => {
        const type: FieldType = this.getValueType(control[0]);
        const rsqlOperator: RSQLOperator = this.getOperator(control[0]);
        this.filtersOutput.filters.push({
          key: control[0],
          value: this.getFormattedValue(control, type),
          valueType: type,
          expression: this.getExpression(control[0]),
          operator: rsqlOperator
        });
      });
    }

    return this.filtersOutput;
  }

  getFormattedValue(control, type: FieldType) {
    switch (type) {
      case FieldType.DATE:
        return control[1].value ? moment(new Date(control[1].value)).format('YYYY-MM-DDT00:00:00+00:00') : null;
      default:
        return control[1].value;
    }
  }

  protected getExpression(name: any): string {
    return this.filters.find(item => item.field.formControlName === name).expression;
  }

  protected getValueType(name: any): FieldType {
    return this.filters.find(item => item.field.formControlName === name).valueType;
  }

  protected getOperator(name: any): RSQLOperator {
    const operator: RSQLOperator = this.filters.find(item => item.field.formControlName === name).operator;
    return operator ? operator : RSQLOperator.EQUAL;
  }

  /**
   * Checks if the Accept button is disabled. When the filter has observables
   * the user can't apply the filter until ALL the observables are completed.
   *
   * @return true if the button is disabled
   */
  acceptButtonIsDisabled(): boolean {
    return this.observableCount > 0;
  }

  /**
   * Destroy the component
   */
  ngOnDestroy() {
    this.destroy$.next(true);
    // Now let's also unsubscribe from the subject itself:
    this.destroy$.unsubscribe();
  }
}
